import { useLayoutEffect, useRef, useEffect } from "react";
import { postNotification } from "./Notifications";
import { ReadyState } from "react-use-websocket";
import { getTicketing, globalVariables } from "../../global/GlobalVariables";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import moment from "moment-timezone";
import { Buffer } from "buffer";
import {
  getLocalStorage,
  hasCookiePermission,
  removeLocalStorage,
  setLocalStorage,
  setSessionStorage
} from "../layout/cookies/CookieFunctions";
import { datadogLogger } from "./DatadogLogger";
import { datadogRum } from "@datadog/browser-rum";
import tokenRefreshQueue from "./TokenRefreshQueue";

const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");

const colors = {
  red: 0,
  orange: 22,
  yellow: 60,
  green: 120,
  turquoise: 180,
  blue: 240,
  purple: 291,
  pink: 300
};

const currency_symbols = {
  USD: "$", // US Dollar
  CAD: "$",
  AUD: "$",
  NZD: "$",
  EUR: "€", // Euro
  CRC: "₡", // Costa Rican Colón
  GBP: "£", // British Pound Sterling
  ILS: "₪", // Israeli New Sheqel
  INR: "₹", // Indian Rupee
  JPY: "¥", // Japanese Yen
  KRW: "₩", // South Korean Won
  NGN: "₦", // Nigerian Naira
  PHP: "₱", // Philippine Peso
  PLN: "zł", // Polish Zloty
  PYG: "₲", // Paraguayan Guarani
  THB: "฿", // Thai Baht
  UAH: "₴", // Ukrainian Hryvnia
  VND: "₫", // Vietnamese Dong

  MXN: "$",
  BRL: "R$",
  SGD: "S$",
  SEK: "kr",
  DKK: "kr",
  NOK: "kr",

  HUF: "Ft",
  CHF: "Fr"
};

const IS_DEV = getEnvironmentVariable("IS_DEV") === "true";

export function wordStartsWithVowel(word) {
  const vowels = ["A", "E", "I", "O", "U"];

  if (!word?.length) return false;
  return vowels.includes(word.toUpperCase()[0]);
}

export function areObjectsEqual(obj1, obj2) {
  if (obj1 === obj2) {
    return true;
  }

  if (
    obj1 == null ||
    obj2 == null ||
    typeof obj1 !== "object" ||
    typeof obj2 !== "object"
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1?.length !== keys2?.length) {
    return false;
  }

  // Check if values of all keys are equal
  for (let key of keys1) {
    if (!keys2.includes(key) || !areObjectsEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export function getAvailableCurrencies() {
  const ticketingDict = getTicketing();
  return Object.keys(ticketingDict);
}

export function getCurrencySymbol(currency) {
  if (!currency) return "$";
  return currency_symbols[currency.toUpperCase()] || "$";
}

export function getCurrencyAmount(currency, value) {
  return `${getCurrencySymbol((currency || "usd").toUpperCase())}${(
    value || 0
  ).toFixed(2)}`;
}

export async function getTicketFees(queryParams) {
  if (!queryParams?.currency) {
    throw new Error("missing currency");
  }

  return await getData("/transactions/feestructure", queryParams);
}

export function getTicketingFeeSummary(
  qty,
  price,
  fixedFee,
  percentFee,
  absorbFees
) {
  let amount = parseFloat(price);
  let percent = percentFee / 100;
  let fixed = fixedFee;
  let fee = 0;

  if (!absorbFees) {
    fee = (amount + fixed) / (1 - percent) - amount;
  } else {
    fee = amount * percent + fixed;
  }
  fee = Math.round(fee * 100) / 100;
  amount =
    Math.round(
      (absorbFees ? parseFloat(amount) : parseFloat(amount) + parseFloat(fee)) *
        100
    ) / 100;
  let total =
    Math.round(
      (absorbFees ? parseFloat(amount) : parseFloat(amount) - parseFloat(fee)) *
        qty *
        100
    ) / 100;
  return {
    qty,
    amount,
    percent,
    fixed,
    fee,
    total,
    absorbFees
  };
}

export function setCachedItems(key, value) {
  try {
    setLocalStorage(key, value);
    var cachedItems = getLocalStorage("CachedItems");
    cachedItems = cachedItems ? JSON.parse(cachedItems) : {};
    cachedItems[key] = true;
    setLocalStorage("CachedItems", JSON.stringify(cachedItems));
  } catch (err) {}
}

export function deleteCachedItems(key) {
  try {
    removeLocalStorage(key);
    var cachedItems = getLocalStorage("CachedItems");
    cachedItems = cachedItems ? JSON.parse(cachedItems) : {};
    delete cachedItems[key];
    setLocalStorage("CachedItems", JSON.stringify(cachedItems));
  } catch (err) {}
}

export function hsl_col_perc(percent, c1, c2) {
  var a = percent / 100,
    b = (colors[c2] - colors[c1]) * a,
    c = b + colors[c1];

  // Return a CSS HSL string
  return "hsl(" + c + ", 100%, 50%)";
}

export function getResizedFileName(fileName, size) {
  var filenameSplit = splitFilename(fileName);
  if (filenameSplit.ext === "jpg" || filenameSplit.ext === "png") {
    return `resized/${filenameSplit.name}-${size}.${filenameSplit.ext}`;
  }

  return fileName;
}

export function splitUrl(url) {
  if (url) {
    var pos = url.lastIndexOf("/");
    if (pos > 0) {
      return {
        url: url.substring(0, pos + 1),
        filename: url.substring(pos + 1)
      };
    } else {
      return { url: url, filename: "" };
    }
  }
  return { url: "", filename: "" };
}

export function splitFilename(filename) {
  if (filename) {
    var pos = filename.lastIndexOf(".");
    if (pos > 0) {
      return {
        name: filename.substring(0, pos),
        ext: filename.substring(pos + 1)
      };
    } else {
      return { name: filename, ext: "" };
    }
  }
  return { name: "", ext: "" };
}

export function titleCase(str) {
  str = str.toLowerCase().split(" ");
  for (var i = 0; i < str?.length; i++) {
    str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
  }
  return str.join(" ");
}

export function newPath(paramChanges) {
  var paramDict = {};
  if (window.location.search && window.location.search !== "") {
    var existingParams = window.location.search.substr(1).split("&");
    existingParams.forEach((param) => {
      var tmp = param.split("=");
      paramDict[tmp[0]] = tmp[1];
    });
  }
  Object.keys(paramChanges).forEach((key) => {
    if (paramChanges[key] !== undefined && paramChanges[key] !== null)
      paramDict[key] = paramChanges[key];
    else if (!paramChanges[key] && paramDict[key]) delete paramDict[key];
  });
  var path;
  Object.keys(paramDict).forEach((key) => {
    if (!path) {
      path = `${window.location.pathname}?`;
    } else {
      path += "&";
    }
    path += `${key}=${paramDict[key]}`;
  });
  return path;
}

export function findGetParameter(parameterName) {
  var result = null,
    tmp = [];
  if (window.location.search) {
    var items = window.location.search.substr(1).split("&");
    for (var index = 0; index < items?.length; index++) {
      tmp = items[index].split("=");
      if (tmp[0] === parameterName) result = decodeURIComponent(tmp[1]);
    }
  }
  return result;
}

export function getIntegerFromString(str) {
  if (!str) return undefined;

  str = str.trim();
  if (!str) {
    return false;
  }
  str = str.replace(/^0+/, "") || "0";
  var n = Math.floor(Number(str));
  if (n !== Infinity && String(n) === str) return n;
  return undefined;
}

export function cleanEventLocation(event) {
  var eventLocation = "";
  if (event?.gameStoreName && event?.gameStoreName !== "") {
    eventLocation = event.gameStoreName;
  }
  if (event?.city && event?.city !== "") {
    if (eventLocation !== "") {
      eventLocation += ", ";
    }
    eventLocation += event.city;
  }
  if (event?.state && event?.state !== "") {
    if (eventLocation !== "") {
      eventLocation += ", ";
    }
    eventLocation += event.state;
  }
  if (event?.country && event?.country !== "") {
    if (eventLocation !== "") {
      eventLocation += ", ";
    }
    eventLocation += event.country;
  }
  return eventLocation;
}

export function cleanLocation(location) {
  if (!location) return "";
  if (location.isOnline) return location.name;
  return (
    (
      (location.subPremise ? location.subPremise + " / " : "") +
      (location.streetNum || "") +
      " " +
      (location.streetName || "")
    ).trim() +
    (location.city && location.city !== ""
      ? (location.streetNum || location.streetName ? " " : "") + location.city
      : "") +
    ((location.state && location.state !== "") ||
    (location.zip && location.zip !== "")
      ? (location.streetNum || location.streetName || location.city
          ? ", "
          : "") + (location.state + " " + location.zip).trim()
      : "") +
    (location.country && location.country !== ""
      ? (location.streetNum ||
        location.streetName ||
        location.city ||
        location.state
          ? ", "
          : "") + location.country
      : "")
  );
}

export function sortedPlayersArray(players, metrics, overall) {
  const temp = players?.length ? players.slice() : [];

  if (temp?.length) {
    temp.sort(function (a, b) {
      if (overall) {
        if (a?.overallBattlePoints > b?.overallBattlePoints) {
          return -1;
        }
        if (a?.overallBattlePoints < b?.overallBattlePoints) {
          return 1;
        }
        if (a?.FFGBattlePointsSoS > b?.FFGBattlePointsSoS) {
          return -1;
        }
        if (a?.FFGBattlePointsSoS < b?.FFGBattlePointsSoS) {
          return 1;
        }
        return 0;
      } else {
        if (a?.podNum && !b?.podNum) return -1;
        if (!a?.podNum && b?.podNum) return 1;
        if (a?.podNum < b?.podNum) return -1;
        if (a?.podNum > b?.podNum) return 1;

        if (a?.isPod && !b?.isPod) return -1;
        if (!a?.isPod && b?.isPod) return 1;

        if (metrics?.length) {
          for (var i = 0; i < metrics?.length; i++) {
            if (metrics[i].isOn) {
              if (!a?.podNum && !b?.podNum) {
                if (metrics[i].negative) {
                  if (a[metrics[i].key] < b[metrics[i].key]) {
                    return -1;
                  }
                  if (a[metrics[i].key] > b[metrics[i].key]) {
                    return 1;
                  }
                } else {
                  if (a[metrics[i].key] > b[metrics[i].key]) {
                    return -1;
                  }
                  if (a[metrics[i].key] < b[metrics[i].key]) {
                    return 1;
                  }
                }
              } else {
                if (metrics[i].negative) {
                  if (a["pod_" + metrics[i].key] < b["pod_" + metrics[i].key]) {
                    return -1;
                  }
                  if (a["pod_" + metrics[i].key] > b["pod_" + metrics[i].key]) {
                    return 1;
                  }
                } else {
                  if (a["pod_" + metrics[i].key] > b["pod_" + metrics[i].key]) {
                    return -1;
                  }
                  if (a["pod_" + metrics[i].key] < b["pod_" + metrics[i].key]) {
                    return 1;
                  }
                }
              }
            }
          }
        }
      }
      if (a?.bracket_seed && !b?.bracket_seed) return -1;
      if (!a?.bracket_seed && b?.bracket_seed) return 1;
      if (a?.bracket_seed < b?.bracket_seed) return -1;
      if (a?.bracket_seed > b?.bracket_seed) return 1;
      return 0;
    });
  }

  return temp;
}

export function useInterval(callback, delay) {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  useLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return;
    }

    const id = setInterval(() => savedCallback.current(), delay);

    return () => clearInterval(id);
  }, [delay]);
}

export function delay(time) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

export function getDistanceInt(lat, lon, coords) {
  return parseInt(getDistance(lat, lon, coords));
}

export function getDistance(lat, lon, coords) {
  var currentLat = coords?.latitude || 41.850033;
  var currentLon = coords?.longitude || -87.6500523;

  var R = 3958.8; // Radius of the earth in miles, 6371kms
  var dLat = deg2rad(currentLat - lat); // deg2rad below
  var dLon = deg2rad(currentLon - lon);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat)) *
      Math.cos(deg2rad(currentLat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c; // Distance in miles
  return d;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

export function comingSoon(t) {
  postNotification(
    "info",
    t ? t("notifications.success.featureComingSoon") : "Feature Coming Soon"
  );
}

export function camelize(str) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, "");
}

export function camelToTitle(camelCase) {
  return camelCase
    .replace(/([A-Z])/g, (match) => ` ${match}`)
    .replace(/^./, (match) => match.toUpperCase())
    .trim();
}

export function isString(value) {
  return typeof value === "string" || value instanceof String;
}

export function validateMetric(value) {
  if (isString(value)) {
    if (value.indexOf(".") === -1) {
      return parseInt(value);
    } else {
      return parseFloat(value);
    }
  }
  return value;
}

export function csvToArray(strData, strDelimiter) {
  // Check to see if the delimiter is defined. If not,
  // then default to comma.
  strDelimiter = strDelimiter || ",";

  // Create a regular expression to parse the CSV values.
  var objPattern = new RegExp(
    // Delimiters.
    "(\\" +
      strDelimiter +
      "|\\r?\\n|\\r|^)" +
      // Quoted fields.
      '(?:"([^"]*(?:""[^"]*)*)"|' +
      // Standard fields.
      '([^"\\' +
      strDelimiter +
      "\\r\\n]*))",
    "gi"
  );

  // Create an array to hold our data. Give the array
  // a default empty first row.
  var arrData = [[]];

  // Create an array to hold our individual pattern
  // matching groups.
  var arrMatches = null;

  // Keep looping over the regular expression matches
  // until we can no longer find a match.
  while ((arrMatches = objPattern.exec(strData))) {
    // Get the delimiter that was found.
    var strMatchedDelimiter = arrMatches[1];

    // Check to see if the given delimiter has a length
    // (is not the start of string) and if it matches
    // field delimiter. If id does not, then we know
    // that this delimiter is a row delimiter.
    if (strMatchedDelimiter.length && strMatchedDelimiter !== strDelimiter) {
      // Since we have reached a new row of data,
      // add an empty row to our data array.
      arrData.push([]);
    }

    var strMatchedValue;

    // Now that we have our delimiter out of the way,
    // let's check to see which kind of value we
    // captured (quoted or unquoted).
    if (arrMatches[2]) {
      // We found a quoted value. When we capture
      // this value, unescape any double quotes.
      strMatchedValue = arrMatches[2].replace(new RegExp('""', "g"), '"');
    } else {
      // We found a non-quoted value.
      strMatchedValue = arrMatches[3];
    }

    // Now that we have our value string, let's add
    // it to the data array.
    arrData[arrData.length - 1].push(strMatchedValue);
  }

  // Return the parsed data.
  return arrData;
}

export function csvToObjectArray(strData, strDelimiter) {
  var array = csvToArray(strData, strDelimiter);
  var keys = array.shift();
  for (let i = 0; i < keys.length; i++) {
    let key = keys[i];
    if (key !== undefined && key !== "") {
      keys[i] = camelize(key);
    }
  }
  var objects = [];
  for (let item of array) {
    let object = {};
    for (let i = 0; i < item.length; i++) {
      let key = keys[i]?.trim();
      if (key !== undefined && key !== "" && item[i] !== "") {
        if (object[key] === undefined) {
          object[key] = item[i];
        }
      }
    }
    if (Object.keys(object).length) {
      objects.push(object);
    }
  }
  return {
    keys: keys,
    items: objects
  };
}

export function validateEmail(email) {
  var regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return !(!email || email.trim() === "" || !regex.test(email.trim()));
}

export function urlToData(url) {
  return new Promise(function (resolve, reject) {
    var xhr = new XMLHttpRequest();
    xhr.onload = function () {
      var reader = new FileReader();
      reader.onloadend = function () {
        resolve(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open("GET", url);
    xhr.responseType = "blob";
    xhr.send();
  });
}

export async function postData(
  route = "",
  data = {},
  apiName = "API",
  headers,
  customUrl,
  allowRetry = true
) {
  const globalVars = globalVariables();
  const { API } = globalVars.amplify;

  // Finds endpoints and headers
  let apiUrl;
  let customHeader;

  for (const apiEndpoint of API?.endpoints || []) {
    if (apiEndpoint?.name === apiName) {
      const header = await apiEndpoint?.custom_header();

      if (header) {
        apiUrl = apiEndpoint?.endpoint;
        customHeader = headers ? headers : header;
        break;
      }
    }
  }

  if (customUrl) {
    apiUrl = customUrl;
  }

  // Converts existing api calls using the { body: {...body} } format to simply {...body}
  if (data?.body) {
    data = { ...data.body };
  }

  const fetchObj = {
    method: "POST", // *GET, POST, PUT, DELETE, etc.
    mode: "cors", // no-cors, *cors, same-origin
    cache: "default", // *default, no-cache, reload, force-cache, only-if-cached
    credentials: "same-origin", // include, *same-origin, omit
    headers: {
      "Content-Type": "application/json",
      env: COMPANY_FLAG,
      ...customHeader
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
    redirect: "follow", // manual, *follow, error
    referrerPolicy: "no-referrer" // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    // body: , // body data type must match "Content-Type" header
  };

  // Prevents sending empty body
  if (data && Object?.keys(data)?.length) {
    fetchObj.body = JSON.stringify(data); // body data type must match "Content-Type" header
  }

  try {
    // Fetch options, default options are marked with *
    const response = await fetch(`${apiUrl}${route}`, fetchObj);
    const json = await response.json();

    if (response?.ok) {
      return json;
    } else {
      // If the error is due to an expired token, refresh tokens and retry
      if (response?.status === 401 && allowRetry && route !== "/oauth/token") {
        // Note: We don't retry the /oauth/token route to avoid infinite loops
        datadogLogger.logForceLogout({
          reason: "api_authentication_failure",
          trigger: "api_call",
          apiEndpoint: route,
          httpStatus: 401,
          component: "postData"
        });
        await tokenRefreshQueue.refreshTokens();
        return await postData(route, data, apiName, headers, customUrl, false);
      }

      const error = new Error(
        `${response?.status}: ${json?.error || "Unexpected error occurred"}`
      );
      error.name =
        response?.status >= 400 && response?.status < 500
          ? "Error"
          : "API Error";
      error.status = response?.status;
      error.response = response;

      if (
        ((response?.status >= 300 && response?.status <= 399) ||
          response?.status >= 500) &&
        !IS_DEV
      ) {
        if (hasCookiePermission("dataDog") && datadogRum?.addError) {
          datadogRum.addError(error);
        }
      }

      throw error;
    }
  } catch (err) {
    if (
      ((err?.response?.status >= 300 && err?.response?.status <= 399) ||
        err?.response?.status >= 500) &&
      !IS_DEV
    ) {
      if (hasCookiePermission("dataDog") && datadogRum?.addError) {
        datadogRum.addError(err);
      }
    }
    throw err;
  }
}

export async function getData(
  route = "",
  params = {},
  apiName = "API",
  headers,
  customUrl,
  allowRetry = true
) {
  const globalVars = globalVariables();
  const { API } = globalVars.amplify;
  // Finds endpoints and headers
  let apiUrl;
  let customHeader;

  for (const apiEndpoint of API?.endpoints || []) {
    if (apiEndpoint?.name === apiName) {
      const header = await apiEndpoint?.custom_header();

      if (header) {
        apiUrl = apiEndpoint?.endpoint;
        customHeader = headers ? headers : header;
        break;
      }
    }
  }

  if (customUrl) {
    apiUrl = customUrl;
  }

  // Converts existing api calls using the { queryStringParameter: {...params} } format to simply {...params}
  if (params?.queryStringParameters) {
    params = { ...params.queryStringParameters };
  }

  let arrayParams = {};
  for (let queryKey of Object.keys(params)) {
    if (queryKey.endsWith("[]")) {
      let queryParam = "";
      if (params?.[queryKey] && Array.isArray(params?.[queryKey])) {
        queryParam = params[queryKey];
        delete params[queryKey];
      }
      arrayParams[queryKey] = queryParam;
    }
  }

  // Creates url
  let newUrl =
    apiUrl +
    route +
    (Object.keys(params)?.length
      ? `?${new URLSearchParams({ ...params })}`
      : "");

  for (let queryKey of Object.keys(arrayParams)) {
    let tempString = arrayParams[queryKey].toString();
    tempString = tempString.replace(
      /,/g,
      `&${queryKey.substring(0, queryKey?.length - 2)}%5B%5D=`
    );
    tempString = `&${queryKey.substring(0, queryKey?.length - 2)}%5B%5D=${tempString}`;

    if (!Object.keys(params)?.length) {
      newUrl += "?";
    }
    newUrl += tempString;
  }

  try {
    const response = await fetch(newUrl, {
      method: "GET",
      mode: "cors",
      cache: "default",
      headers: {
        "Content-Type": "application/json",
        "Accept-Encoding": "gzip",
        env: COMPANY_FLAG,
        ...customHeader
      }
    });

    const json = await response.json();

    if (response?.ok) {
      return json;
    } else {
      // If the error is due to an expired token, refresh tokens and retry
      if (response?.status === 401 && allowRetry && route !== "/oauth/token") {
        // Note: We don't retry the /oauth/token route to avoid infinite loops
        datadogLogger.logForceLogout({
          reason: "api_authentication_failure",
          trigger: "api_call",
          apiEndpoint: route,
          httpStatus: 401,
          component: "getData"
        });
        await tokenRefreshQueue.refreshTokens();
        return await getData(route, params, apiName, headers, customUrl, false);
      }

      const error = new Error(
        `${response?.status}: ${json?.error || "Unexpected error occurred"}`
      );
      error.name =
        response?.status >= 400 && response?.status < 500
          ? "Error"
          : "API Error";
      error.status = response?.status;
      error.response = response;

      const ratelimitReset = response.headers.get("ratelimit-reset");
      if (ratelimitReset > 0) {
        setSessionStorage(
          "ratelimit-reset",
          moment().add(ratelimitReset, "seconds").toISOString()
        );
      }

      if (
        ((response?.status >= 300 && response?.status <= 399) ||
          response?.status >= 500) &&
        !IS_DEV
      ) {
        if (hasCookiePermission("dataDog") && datadogRum?.addError) {
          datadogRum.addError(error);
        }
      }

      throw error;
    }
  } catch (err) {
    if (
      ((err?.response?.status >= 300 && err?.response?.status <= 399) ||
        err?.response?.status >= 500) &&
      !IS_DEV
    ) {
      if (hasCookiePermission("dataDog") && datadogRum?.addError) {
        datadogRum.addError(err);
      }
    }
    throw err;
  }
}

export async function deleteData(
  route = "",
  data = {},
  apiName = "API",
  headers,
  customUrl,
  allowRetry = true
) {
  const globalVars = globalVariables();
  const { API } = globalVars.amplify;
  // Finds endpoints and headers
  let apiUrl;
  let customHeader;

  for (const apiEndpoint of API?.endpoints || []) {
    if (apiEndpoint?.name === apiName) {
      const header = await apiEndpoint?.custom_header();

      apiUrl = apiEndpoint?.endpoint;
      if (header) {
        customHeader = headers ? headers : header;
        break;
      }
    }
  }

  if (customUrl) {
    apiUrl = customUrl;
  }

  // Converts existing api calls using the { body: {...body} } format to simply {...body}
  if (data?.body) {
    data = { ...data.body };
  }

  try {
    const response = await fetch(`${apiUrl}${route}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        env: COMPANY_FLAG,
        ...customHeader
      },
      body: JSON.stringify(data)
    });

    const json = await response.json();

    if (response?.ok) {
      return json;
    } else {
      // If the error is due to an expired token, refresh tokens and retry
      if (response?.status === 401 && allowRetry && route !== "/oauth/token") {
        // Note: We don't retry the /oauth/token route to avoid infinite loops
        datadogLogger.logForceLogout({
          reason: "api_authentication_failure",
          trigger: "api_call",
          apiEndpoint: route,
          httpStatus: 401,
          component: "deleteData"
        });
        await tokenRefreshQueue.refreshTokens();
        return await deleteData(
          route,
          data,
          apiName,
          headers,
          customUrl,
          false
        );
      }

      const error = new Error(
        `${response?.status}: ${json?.error || "Unexpected error occurred"}`
      );
      error.name =
        response?.status >= 400 && response?.status < 500
          ? "Error"
          : "API Error";
      error.status = response?.status;
      error.response = response;

      if (
        ((response?.status >= 300 && response?.status <= 399) ||
          response?.status >= 500) &&
        !IS_DEV
      ) {
        if (hasCookiePermission("dataDog") && datadogRum?.addError) {
          datadogRum.addError(error);
        }
      }

      throw error;
    }
  } catch (err) {
    if (
      ((err?.response?.status >= 300 && err?.response?.status <= 399) ||
        err?.response?.status >= 500) &&
      !IS_DEV
    ) {
      if (hasCookiePermission("dataDog") && datadogRum?.addError) {
        datadogRum.addError(err);
      }
    }
    throw err;
  }
}

// Used to retrieve auth token for websockets
export async function getWSAuth(params) {
  try {
    if (params) {
      const webSocketAuth = await getData("/auth/websockettoken", {
        ...params
      });

      return webSocketAuth.token;
    } else {
      throw new Error("Missing websocket params");
    }
  } catch (err) {
    throw new Error(err);
  }
}

// Retrieves websocket connection status when passing in useWebsocket().readyState
export function getWSConnectionState(readyState) {
  const connectionStates = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated"
  };
  return connectionStates[readyState] || "Unknown";
}

export function removeParam(key, sourceURL) {
  let returnString = sourceURL.split("?")[0],
    param,
    paramsArray = [],
    queryString = sourceURL.indexOf("?") !== -1 ? sourceURL.split("?")[1] : "";
  if (queryString !== "") {
    paramsArray = queryString.split("&");
    for (let i = paramsArray?.length - 1; i >= 0; i -= 1) {
      param = paramsArray[i].split("=")[0];
      if (param === key) {
        paramsArray.splice(i, 1);
      }
    }
    if (paramsArray?.length)
      returnString = returnString + "?" + paramsArray.join("&");
  }
  return returnString;
}

export function getTicketingFeesByCurrency(currency) {
  const ticketingDict = getTicketing();
  return ticketingDict[currency];
}

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Convert hex codes to rgb
export function hexToRGB(hex) {
  const fullHex = (hexCode) => {
    let r = hexCode.slice(1, 2);
    let g = hexCode.slice(2, 3);
    let b = hexCode.slice(3, 4);

    r = parseInt(r + r, 16);
    g = parseInt(g + g, 16);
    b = parseInt(b + b, 16);

    return { r, g, b };
  };

  if (hex?.length === 4) {
    return fullHex(hex);
  }

  const r = parseInt(hex?.slice(1, 3), 16);
  const g = parseInt(hex?.slice(3, 5), 16);
  const b = parseInt(hex?.slice(5, 7), 16);

  // return {r, g, b}
  return `${r}, ${g}, ${b}`;
}

export function isUsingWS(authContext) {
  const useWS =
    getEnvironmentVariable("USE_WEBSOCKET") !== "false" ? true : false;

  return useWS;
}

export function getTimezoneDisplay(date) {
  const userTimezone = moment.tz.guess();
  const now = moment();
  const isDST = now.isDST();

  const tzAbbr = moment.tz(userTimezone).format("z");

  switch (userTimezone) {
    case "Pacific/Baker_Island":
      return "BIT";
    case "Pacific/Midway":
      return "SST";
    case "Pacific/Pago_Pago":
      return "SST";
    case "Pacific/Honolulu":
      return "HST";
    case "Pacific/Rarotonga":
      return "CKT";
    case "Pacific/Tahiti":
      return "TAHT";
    case "Pacific/Marquesas":
      return "MIT";
    case "Pacific/Gambier":
      return "GIT";
    case "America/Anchorage":
      return isDST ? "AKDT" : "AKST";
    case "Pacific/Pitcairn":
      return "PST";
    case "America/Los_Angeles":
      return isDST ? "PDT" : "PST";
    case "America/Denver":
      return isDST ? "MDT" : "MST";
    case "America/Chicago":
      return isDST ? "CDT" : "CST";
    case "America/New_York":
      return isDST ? "EDT" : "EST";
    case "America/Halifax":
      return isDST ? "ADT" : "AST";
    case "America/St_Johns":
      return isDST ? "NDT" : "NST";
    case "America/Sao_Paulo":
      return isDST ? "BRST" : "BRT";
    case "America/Noronha":
      return "FNT";
    case "Atlantic/Cape_Verde":
      return "CVT";
    case "Atlantic/Azores":
      return isDST ? "AZOST" : "AZOT";
    case "UTC":
      return "UTC";
    case "Europe/London":
      return isDST ? "BST" : "GMT";
    case "Europe/Paris":
    case "Europe/Berlin":
    case "Europe/Rome":
    case "Europe/Madrid":
      return isDST ? "CEST" : "CET";
    case "Europe/Athens":
    case "Europe/Helsinki":
    case "Europe/Istanbul":
      return isDST ? "EEST" : "EET";
    case "Europe/Moscow":
      return isDST ? "MSK" : "MSK";
    case "Asia/Dubai":
      return "GST";
    case "Asia/Karachi":
      return "PKT";
    case "Asia/Kolkata":
      return "IST";
    case "Asia/Dhaka":
      return "BST";
    case "Asia/Bangkok":
      return "ICT";
    case "Asia/Hong_Kong":
    case "Asia/Shanghai":
      return "CST";
    case "Asia/Tokyo":
      return "JST";
    case "Asia/Seoul":
      return "KST";
    case "Australia/Perth":
      return "AWST";
    case "Australia/Darwin":
      return "ACST";
    case "Australia/Adelaide":
      return isDST ? "ACDT" : "ACST";
    case "Australia/Brisbane":
      return "AEST";
    case "Australia/Sydney":
    case "Australia/Melbourne":
      return isDST ? "AEDT" : "AEST";
    case "Pacific/Auckland":
      return isDST ? "NZDT" : "NZST";
    case "Pacific/Chatham":
      return isDST ? "CHADT" : "CHAST";
    case "Pacific/Apia":
      return isDST ? "WSDT" : "WST";
    case "Pacific/Kiritimati":
      return "LINT";

    default:
      return tzAbbr;
  }
}

export async function googleTimezoneApi(object) {
  if (!object?.location) {
    throw new Error("Missing location");
  }

  let { location } = object;
  const key = object?.key || getEnvironmentVariable("GOOGLE_API_KEY");
  const timestamp = object?.timestamp || moment().unix();

  // Replace commas with encoded commas
  if (location.includes(",")) {
    location = location.replace(",", "%2C");
  }

  const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${location}&timestamp=${timestamp}&key=${key}`;

  try {
    const response = await fetch(url);
    const json = await response.json();

    if (response.status === 200) {
      return json;
    } else {
      throw new Error(json.error);
    }
  } catch (err) {
    throw new Error(err);
  }
}

export function utf8ToBase64(string) {
  // Browser logic
  if (typeof window !== "undefined") {
    const utf8Bytes = new TextEncoder().encode(string);
    let binary = "";
    utf8Bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
    return window.btoa(binary);
  } else {
    // Node.js/incompatible browser fallback
    return Buffer.from(string, "utf8").toString("base64");
  }
}

export function base64ToUtf8(string) {
  // Browser logic
  if (typeof window !== "undefined") {
    const binary = window.atob(string);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } else {
    // Node.js/incompatible browser fallback
    return Buffer.from(string, "base64").toString("utf8");
  }
}
